import type { ComponentMaterialGroup } from "@stryker/core/types/jobs";
import { type ChangeEvent, Fragment } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";

import type { SingleComponentData } from "../types";

type FieldErrors = {
  component_nps?: string;
  component_schedule?: string;
  component_diameter?: string;
  unit_for_component_diameter?: string;
};

type StandardComponentProps = {
  components_data: SingleComponentData[];
  set_components_data: React.Dispatch<React.SetStateAction<SingleComponentData[]>>;
  component_material: number | null;
  setComponent_material: React.Dispatch<React.SetStateAction<number | null>>;
  materialGroups: ComponentMaterialGroup[];
  materialError?: string | null;
  componentError?: FieldErrors;
};

const StandardComponent = ({
  components_data,
  set_components_data,
  component_material,
  setComponent_material,
  materialGroups,
  materialError,
  componentError,
}: StandardComponentProps) => {
  const { npsOptions, getScheduleOptions, measureUnitOptions } = useConfigOptions();

  const handleChangeData = (value: unknown, place: string) =>
    set_components_data((prevState) => {
      return prevState.map((p, i) => {
        if (i === 0) {
          return {
            ...p,
            [place]: value,
          };
        }
        return p;
      });
    });

  const component = components_data[0];
  const scheduleOptions = component.component_nps !== "custom" ? getScheduleOptions(component.component_nps) : [];
  const fieldWidth = "w-full sm:w-[270px]";

  !npsOptions.some((d) => d.title === "custom") && npsOptions.unshift({ title: "custom", value: "custom" });

  return (
    <div className="flex flex-col">
      <Label className="mb-1">
        Component Material <span className="text-red-500">*</span>
      </Label>
      <div className={fieldWidth}>
        <Select
          value={component_material !== null ? String(component_material) : undefined}
          onValueChange={(option) => {
            setComponent_material(Number(option));
          }}
        >
          <SelectTrigger
            className={cn(
              "w-full",
              !component_material && "[&>span]:text-gray-400",
              materialError && "border-error-300"
            )}
          >
            <SelectValue placeholder="Component Material" className="text-red-950" />
          </SelectTrigger>
          <SelectContent>
            {materialGroups.map((group, groupIndex) => (
              <Fragment key={group.group}>
                {groupIndex > 0 && <SelectSeparator />}
                <SelectGroup>
                  <SelectLabel>{group.group}</SelectLabel>
                  {group.options.map((option) => {
                    const descriptor = option.type ?? option.general_material;
                    const label = descriptor ? `${option.material} - ${descriptor}` : option.material;
                    return (
                      <SelectItem key={option.id} value={String(option.id)}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </Fragment>
            ))}
          </SelectContent>
        </Select>
      </div>
      {materialError ? (
        <Typography className="mt-1 text-error-500" variant="text-small">
          {materialError}
        </Typography>
      ) : null}

      <div className="my-2 flex flex-wrap gap-4 pb-2">
        <div className="my-2 flex w-full items-end gap-4">
          <Typography variant="display-xs" weight="semibold">
            Component
          </Typography>
        </div>

        <div className={cn("flex flex-col", fieldWidth)}>
          <Label className="mb-1">NPS 1</Label>
          <Select
            value={component.component_nps ?? undefined}
            onValueChange={(option) => {
              handleChangeData(option, "component_nps");
              if (option === "custom") {
                // Clear schedule if switching to custom NPS
                handleChangeData(null, "component_schedule");
              } else {
                // Clear custom diameter fields when switching away from custom
                handleChangeData(null, "component_diameter");
                handleChangeData(null, "unit_for_component_diameter");
              }
            }}
          >
            <SelectTrigger
              className={cn(
                "w-full",
                !component.component_nps && "[&>span]:text-gray-400",
                componentError?.component_nps && "border-error-300"
              )}
            >
              <SelectValue placeholder="NPS" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>NPS</SelectLabel>
                {npsOptions.map((option) => {
                  return (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.title}
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
          {componentError?.component_nps ? (
            <Typography className="mt-1 text-error-500" variant="text-small">
              {componentError.component_nps}
            </Typography>
          ) : null}
        </div>

        {component.component_nps === "custom" ? (
          <>
            <TextInput
              className={cn(fieldWidth, "gap-0")}
              label="Custom Diameter"
              type="number"
              name="component_diameter"
              step={inputStep}
              required={true}
              max={maxInputNumber}
              value={component.component_diameter ?? ""}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleChangeData(event.target.value, "component_diameter");
              }}
              error={componentError?.component_diameter}
            />
            <div className={cn("flex flex-col", fieldWidth)}>
              <Label className="mb-1">Unit</Label>
              <Select
                value={component.unit_for_component_diameter ?? undefined}
                onValueChange={(option) => {
                  handleChangeData(option, "unit_for_component_diameter");
                }}
              >
                <SelectTrigger
                  className={cn(
                    "w-full",
                    !component.unit_for_component_diameter && "[&>span]:text-gray-400",
                    componentError?.unit_for_component_diameter && "border-error-300"
                  )}
                >
                  <SelectValue placeholder="Unit for Custom Diameter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Unit for Custom Diameter</SelectLabel>
                    {measureUnitOptions.map((option) => {
                      return (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.title}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {componentError?.unit_for_component_diameter ? (
                <Typography className="mt-1 text-error-500" variant="text-small">
                  {componentError.unit_for_component_diameter}
                </Typography>
              ) : null}
            </div>
          </>
        ) : null}
        {component.component_nps !== "custom" ? (
          <div className={cn("flex flex-col", fieldWidth)}>
            <Label className="mb-1">Schedule 1</Label>
            <Select
              value={component.component_schedule ? String(component.component_schedule) : undefined}
              onValueChange={(option) => {
                handleChangeData(option, "component_schedule");
              }}
            >
              <SelectTrigger
                className={cn(
                  "w-full",
                  !component.component_schedule && "[&>span]:text-gray-400",
                  componentError?.component_schedule && "border-error-300"
                )}
              >
                <SelectValue placeholder="Schedule" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Schedule</SelectLabel>
                  {scheduleOptions.map((option) => {
                    return (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.title}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
            {componentError?.component_schedule ? (
              <Typography className="mt-1 text-error-500" variant="text-small">
                {componentError.component_schedule}
              </Typography>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StandardComponent;
